import {
  Box,
  Button,
  Grid,
  Stack,
  useTheme,
  Paper,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShareLink from "./components/ShareLink";
import ClientInfoCard from "./components/ClientInfoCard";
import BookingContactsCard from "./components/BookingContactsCard";
import DocumentsSection from "./components/DocumentsSection";
import StreamSection from "./components/StreamSection";
import Sidebar from "./components/Sidebar";
import ServiceCard from "./components/ServiceCard";
import AIWhatsAppDrawer from "./components/AIWhatsAppDrawer";
import { useBookingData } from "../../hooks/UseBookingData";
import { DocumentItem } from "../../types/documents";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookingData } from "../../../types/view-booking";
import sharemessagegraphicpattern from "../../../../stream-management/components/share-message/pattern.webp";
import muzukurulogo from "../../../../../assets/muzukurublack.png";
import BookingInfoCard from "./components/BookingInfoCard";
import BookingAuditCards from "./components/BookingAuditCards";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import TemplateSelectorDialog from "../../../../stream-management/components/memorial-page/components/shared-ui/TemplateSelectorDialog";
import { BaseURL } from "../../../../../service/Api";
import Invoice from "../../components/invoice/Invoice";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../../constants/permissions";

type BookingStream = BookingData["streams"][number];
type UIStream = {
  id: string;
  title: string;
  date: string;
  time: string;
  locationName: string;
  locationAddress: string;
};

const BookingDetails = () => {
  const { bookingData, documents } = useBookingData();
  const { id } = useParams<{ id: string }>();
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  useEffect(() => {
    setDocs(documents);
  }, [documents]);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isStaging = BaseURL.includes("staging.muzukuru.com");
  const [streamTab, setStreamTab] = useState(0);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiDrawerMode, setAiDrawerMode] = useState<
    "review" | "thankyou" | "anniversary" | null
  >(null);

  const user = useSelector((state: RootState) => state.auth.user);
  const { hasPermission } = usePermissions();
  const canEditBooking = hasPermission(PERMISSIONS.BOOKING_CHANGE);
  const canAddDocument = hasPermission(PERMISSIONS.BOOKING_CHANGE);
  const canDeleteDocument = hasPermission(PERMISSIONS.BOOKING_CHANGE);

  const streams = bookingData?.streams ?? [];
  const streamTabs = streams.map((_, idx) => `Stream ${idx + 1}`);
  const activeStream = streams[streamTab];
  const currentStreamId = activeStream?.id;

  const handleEdit = () => {
    if (!id) return;

    navigate(`/bookings/${id}/edit`);
  };

  const handleViewStream = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}`);
  };

  const handleOpenCallsheet = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}/callsheet/`);
  };

  const buildMemorialUrl = () => {
    if (!id) return "/";

    const streamTitle = activeStream?.title || "";
    const prefix = "Stream for ";
    const nameForSlug = streamTitle.startsWith(prefix)
      ? streamTitle.slice(prefix.length)
      : streamTitle;
    const slug = encodeURIComponent(nameForSlug.trim().replace(/\s+/g, "-"));

    if (bookingData?.service === "Conference") {
      const conferenceSlug = encodeURIComponent(
        bookingData.conferenceDetails?.conference_title?.replace(/\s+/g, "-") ||
          "conference",
      );
      return `/conference-page/${id}/${conferenceSlug}`;
    }

    if (bookingData?.service === "Church") {
      const churchTitle = encodeURIComponent(
        bookingData.churchDetails?.church_name?.replace(/\s+/g, "-") ||
          "church",
      );
      return `/church-page/${id}/${churchTitle}`;
    }

    return `/memorial-page/${id}/${slug}`;
  };

  const handleOpenMemorialPage = () => {
    if (!id || !currentStreamId) return;
    if (!isStaging) {
      navigate(buildMemorialUrl());
      return;
    }
    if (activeStream?.template_id) {
      navigate(buildMemorialUrl());
    } else {
      setTemplateDialogOpen(true);
    }
  };

  const toUIStream = (s: BookingStream): UIStream => ({
    id: String(s.id ?? ""),
    title: s.title,
    date: s.date,
    time: `${s.start?.slice(0, 5)} - ${s.end?.slice(0, 5)}`,
    locationName: s.venueDetails?.parent
      ? `${s.venueDetails.parent.name} — ${s.venueDetails.name}`
      : (s.venueDetails?.name ?? "N/A"),
    locationAddress: [s.addressOne, s.addressTwo, s.suburb, s.city]
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(", "),
  });

  const uiStreams = streams.map(toUIStream);
  const uiActiveStream = uiStreams[streamTab];

  return (
    <Box sx={{ width: "100%" }}>
      {canEditBooking && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            onClick={handleEdit}
            variant="contained"
            sx={{
              bgcolor: colors.primary[400],
              color: colors.primary[900],
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Edit Booking
          </Button>
        </Box>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 9 }}>
          <Stack spacing={2}>
            <ShareLink
              streamLink={activeStream?.website_stream_link}
              fullName={bookingData?.beloved?.name || "N/A"}
              birthDate={bookingData?.beloved?.details?.dateOfBirth || "N/A"}
              deathDate={bookingData?.beloved?.details?.dateOfDeath || "N/A"}
              funeralDateText={activeStream?.date || "N/A"}
              venueText={
                [
                  activeStream?.venueDetails?.name,
                  activeStream?.addressOne,
                  activeStream?.addressTwo,
                  activeStream?.suburb,
                  activeStream?.city,
                ]
                  .filter((value): value is string =>
                    Boolean(value && value.trim()),
                  )
                  .filter(
                    (value, index, all) =>
                      all.findIndex(
                        (v) =>
                          v.trim().toLowerCase() === value.trim().toLowerCase(),
                      ) === index,
                  )
                  .join(", ") || "N/A"
              }
              imageUrl={bookingData?.beloved?.avatar || "/default-avatar.jpg"}
              patternSvgUrl={sharemessagegraphicpattern}
              funeralStartTime={activeStream?.start || "N/A"}
              livestreamedBy={muzukurulogo}
              serviceType={bookingData?.service}
              funeralHomeName={bookingData?.funeralHome?.name}
            />

            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <ClientInfoCard
                    client={bookingData?.organiser}
                    currentUserName={user?.first_name + " " + user?.last_name}
                    onViewAllBookings={() =>
                      navigate(
                        `/contacts/${bookingData?.organiser?.contact_type}/view/${bookingData?.organiser?.id}`,
                      )
                    }
                    loading={!bookingData}
                    bookingType={bookingData?.booking_type}
                    funeralHome={bookingData?.funeralHome}
                    bookingData={bookingData}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <BookingInfoCard
                    bookingData={bookingData}
                    loading={!bookingData}
                  />
                </Box>
              </Grid>
            </Grid>

            {(!bookingData || (bookingData.contacts?.length ?? 0) > 0) && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <BookingContactsCard
                    contacts={bookingData?.contacts}
                    loading={!bookingData}
                  />
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <DocumentsSection
                    documents={docs}
                    bookingId={id ?? ""}
                    onAddDocument={(newDoc) =>
                      setDocs((prev) => [newDoc, ...prev])
                    }
                    loading={!bookingData}
                    onViewInvoice={() => setInvoiceOpen(true)}
                    canViewInvoice={!!currentStreamId}
                    canAddDocument={canAddDocument}
                    canDeleteDocument={canDeleteDocument}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <ServiceCard
                    name={bookingData?.package?.name || ""}
                    price={bookingData?.package?.price || 0}
                    items={bookingData?.package?.items || []}
                    total={bookingData?.package?.total || ""}
                    loading={!bookingData}
                  />
                </Box>
              </Grid>
            </Grid>

            {!bookingData ? (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #f0f4f8",
                  borderRadius: "14px",
                  bgcolor: "background.paper",
                }}
              >
                <Stack direction="row" spacing={1} mb={3}>
                  <Box
                    sx={{
                      width: 80,
                      height: 28,
                      bgcolor: "action.hover",
                      borderRadius: "10px",
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                  <Box
                    sx={{
                      width: 80,
                      height: 28,
                      bgcolor: "action.hover",
                      borderRadius: "10px",
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 150,
                      bgcolor: "action.hover",
                      borderRadius: "12px",
                      p: 2,
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                  <Stack spacing={1.5} sx={{ width: { xs: "100%", md: 320 } }}>
                    <Box
                      sx={{
                        height: 68,
                        bgcolor: "action.hover",
                        borderRadius: "14px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                    <Box
                      sx={{
                        height: 68,
                        bgcolor: "action.hover",
                        borderRadius: "14px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                    <Box
                      sx={{
                        height: 68,
                        bgcolor: "action.hover",
                        borderRadius: "14px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <StreamSection
                streamTabs={streamTabs}
                stream={uiActiveStream}
                bookingId={id ?? ""}
                activeTab={streamTab}
                onTabChange={setStreamTab}
                onViewStream={handleViewStream}
                onOpenMemorialPage={handleOpenMemorialPage}
                onOpenCallsheet={handleOpenCallsheet}
                serviceType={bookingData?.service}
              />
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Stack spacing={2}>
            <BookingAuditCards bookingData={bookingData} />

            <Sidebar
              rating={bookingData?.completeness_score || 0}
              smartActions={[]}
              actionItems={[]}
              mode="ratingOnly"
            />

            <Box sx={{ mb: 12 }}>
              <Sidebar
                rating={70}
                smartActions={[]}
                actionItems={[]}
                mode="bookingSidebar"
                bookingData={bookingData}
                onOpenAIAssistant={(mode) => {
                  setAiDrawerMode(mode);
                  setAiDrawerOpen(true);
                }}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <AIWhatsAppDrawer
        open={aiDrawerOpen}
        onClose={() => {
          setAiDrawerOpen(false);
          setAiDrawerMode(null);
        }}
        bookingData={bookingData}
        currentUserName={
          user
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : "Muzukuru Staff"
        }
        initialMode={aiDrawerMode}
      />

      {!aiDrawerOpen && (
        <Paper
          elevation={6}
          onClick={() => setAiDrawerOpen(true)}
          sx={{
            position: "fixed",
            bottom: 90,
            right: 24,
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: "30px",
            bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            color: colors.primary[700],
            cursor: "pointer",
            border: `1.5px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            transition: "transform 0.2s ease, boxShadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 30px rgba(0,0,0,0.2)",
              borderColor: "#724E41",
            },
          }}
        >
          <AutoAwesomeIcon sx={{ color: "#724E41", fontSize: 20 }} />
          <Typography
            fontWeight={700}
            variant="body2"
            sx={{ color: colors.primary[700] }}
          >
            AI Assistant
          </Typography>
        </Paper>
      )}

      {isStaging && currentStreamId && (
        <TemplateSelectorDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          serviceType={bookingData?.service ?? "Funeral"}
          streamId={currentStreamId}
          currentTemplateId={activeStream?.template_id}
          onConfirm={() => {
            setTemplateDialogOpen(false);
            navigate(buildMemorialUrl());
          }}
        />
      )}

      {currentStreamId && (
        <Invoice
          open={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          streamId={currentStreamId.toString()}
        />
      )}
    </Box>
  );
};

export default BookingDetails;
